/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Injectable, OnDestroy, Inject } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SwaggerStateService } from './swagger-state.service';
import { SwaggerDomService } from './swagger-dom.service';
import { SwaggerRequest } from './models/swagger-state.model';
import { ApimProxyConfig } from './models/apim-config.model';
import { APIM_CONFIG_TOKEN } from './apim-config.token';

/**
 * Service responsible for intercepting Swagger UI HTTP requests and responses
 * to integrate with Azure API Management (APIM) proxy.
 *
 * This service:
 * - Transforms requests to route through APIM proxy
 * - Adds required authentication headers (Authorization, X-Subscription-Key)
 * - Preserves original request data for display purposes
 * - Injects curl commands into Swagger UI DOM after responses
 *
 * @example
 * ```typescript
 * constructor(private interceptor: ApimInterceptorService) {
 *   const modifiedRequest = this.interceptor.interceptRequest(request);
 *   const modifiedResponse = this.interceptor.interceptResponse(response);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ApimInterceptorService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly config: ApimProxyConfig;

  constructor(
    @Inject(APIM_CONFIG_TOKEN) config: ApimProxyConfig,
    private readonly stateService: SwaggerStateService,
    private readonly domService: SwaggerDomService
  ) {
    this.config = config;
  }

  /**
   * Creates Swagger UI configuration object with interceptors
   * This replaces the legacy ENHANCED_SWAGGER_UI_APIM_CONFIG
   *
   * @returns Configuration object for SwaggerUIBundle
   */
  createSwaggerUIConfig() {
    return {
      /**
       * Request interceptor for SwaggerUI
       */
      requestInterceptor: (request: any) => {
        return this.interceptRequest(request);
      },

      /**
       * Response interceptor for SwaggerUI
       */
      responseInterceptor: (response: any) => {
        return this.interceptResponse(response);
      },

      /**
       * Post-render hook - called once after Swagger UI initialization
       */
      onComplete: () => {
        this.onComplete();
      },

      /**
       * Sets the authentication token via state service
       */
      setAuthContext: (accessToken: string) => {
        this.stateService.setAuthToken(accessToken);
        if (accessToken && accessToken.trim() !== '') {
          console.log('✅ Auth context set for APIM proxy with valid token');
        } else {
          console.warn('⚠️ Auth context set for APIM proxy but token is empty or invalid');
        }
      },

      /**
       * Sets the OpenAPI spec via state service
       */
      setSwaggerSpec: (spec: any) => {
        this.stateService.setSwaggerSpec(spec);
        console.log('✅ Swagger spec set for APIM proxy');
      }
    };
  }

  /**
   * Intercepts outgoing Swagger UI requests to route through APIM proxy
   *
   * Process:
   * 1. Check if request should be proxied (skip spec loading requests)
   * 2. Add Authorization token from state service
   * 3. Add X-Target-Subscription-Key from OpenAPI spec
   * 4. Store original request for display purposes
   * 5. Transform URL to proxy format: baseUrl?target=encodedOriginalUrl
   * 6. Add APIM authentication headers
   *
   * @param request - The original Swagger UI request object
   * @returns Modified request object with proxy URL and headers
   */
  interceptRequest(request: any): any {
    try {
      // Skip spec loading requests
      if (request.loadSpec) {
        return request;
      }

      const originalUrl = request.url;

      // Check if this URL should be proxied
      if (!this.shouldProxyRequest(originalUrl)) {
        return request;
      }

      // Add Authorization token from state service
      const authToken = this.getAuthToken();
      if (authToken && !request.headers['Authorization']) {
        request.headers = request.headers || {};
        request.headers['Authorization'] = `Bearer ${authToken}`;
        console.log('✅ Added Authorization token to request');
      }

      // Add X-Target-Subscription-Key from OpenAPI spec or existing request header
      const targetSubscriptionKey = this.getTargetSubscriptionKey(originalUrl)
        || request.headers?.['X-Subscription-Key'] || null;

      // Create display-friendly request for Swagger UI curl/URL display
      const displayRequest = {
        ...request,
        url: originalUrl,
        method: request.method,
        headers: { ...request.headers },
        body: request.body
      };

      if (targetSubscriptionKey) {
        request.headers = request.headers || {};
        request.headers['X-Target-Subscription-Key'] = targetSubscriptionKey;

        displayRequest.headers = displayRequest.headers || {};
        displayRequest.headers['X-Subscription-Key'] = targetSubscriptionKey;
      }

      // Store original request details before transformation
      const originalRequest: SwaggerRequest = {
        url: originalUrl,
        method: request.method,
        headers: { ...request.headers },
        body: request.body
      };

      // Transform URL to proxy format
      const encodedTarget = encodeURIComponent(originalUrl);
      request.url = `${this.config.baseUrl}?target=${encodedTarget}`;

      // Add APIM proxy authentication header
      request.headers = request.headers || {};
      request.headers['X-Subscription-Key'] = this.config.apimSubscriptionKey;

      // Ensure Authorization token is preserved
      if (originalRequest.headers['Authorization']) {
        request.headers['Authorization'] = originalRequest.headers['Authorization'];
      }

      // Store request info via state service for response interceptor
      this.stateService.addPendingRequest(
        originalUrl,
        originalRequest,
        displayRequest,
        request.url
      );

      return request;

    } catch (error) {
      console.error('❌ Error in request interceptor:', error);
      return request;
    }
  }

  /**
   * Intercepts incoming Swagger UI responses to inject curl commands
   *
   * Process:
   * 1. Retrieve pending request data from state service
   * 2. Inject curl command into Swagger UI DOM using DOM service
   * 3. Schedule delayed injections to catch late-loading DOM elements
   * 4. Clean up pending request from state
   *
   * @param response - The response object from Swagger UI
   * @returns Unmodified response object (pass-through)
   */
  interceptResponse(response: any): any {
    try {
      console.log('📥 Response received, preparing curl injection');

      const displayRequest = this.stateService.getLastDisplayRequest();
      const proxyUrl = this.stateService.getLastProxyUrl();

      if (!displayRequest || !proxyUrl) {
        console.warn('⚠️ No pending request found for response');
        return response;
      }

      // Inject curl command immediately
      this.injectCurlCommand(displayRequest, proxyUrl);

      // Schedule delayed injections to catch late-loading DOM elements
      // Using RxJS timer instead of setTimeout for better control and testability
      this.scheduleDelayedInjections(displayRequest, proxyUrl);

      return response;

    } catch (error) {
      console.error('❌ Error in response interceptor:', error);
      return response;
    }
  }

  /**
   * Callback executed after Swagger UI initialization completes
   */
  onComplete(): void {
    console.log('🎯 Swagger UI initialization complete');
  }

  /**
   * Injects curl command into Swagger UI DOM
   *
   * @param request - Display request data for UI presentation
   * @param proxyUrl - Proxy URL used for the request
   */
  private injectCurlCommand(request: SwaggerRequest, proxyUrl: string): void {
    try {
      this.domService.injectOriginalRequestInfo(request, proxyUrl);
    } catch (error) {
      console.error('❌ Error injecting curl command:', error);
    }
  }

  /**
   * Schedules delayed curl injections to handle late-loading DOM elements
   * Uses RxJS timer instead of setTimeout for better testability
   *
   * @param request - Display request data for UI presentation
   * @param proxyUrl - Proxy URL used for the request
   */
  private scheduleDelayedInjections(request: SwaggerRequest, proxyUrl: string): void {
    const delays = [100, 250, 500]; // milliseconds

    delays.forEach(delay => {
      timer(delay).pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        try {
          this.domService.injectOriginalRequestInfo(request, proxyUrl);
        } catch (error) {
          console.error(`❌ Error in delayed injection (${delay}ms):`, error);
        }
      });
    });
  }

  /**
   * Retrieves Authorization token from state service
   *
   * @returns Authorization token or null if not available
   */
  private getAuthToken(): string | null {
    try {
      const token = this.stateService.getAuthToken();
      if (token) {
        return token;
      }

      // Fallback: try to get from last request
      const lastRequest = this.stateService.getLastOriginalRequest();
      if (lastRequest?.headers?.['Authorization']) {
        const authHeader = lastRequest.headers['Authorization'];
        if (authHeader.startsWith('Bearer ')) {
          return authHeader.substring(7);
        }
      }

      return null;

    } catch (error) {
      console.error('❌ Error retrieving auth token:', error);
      return null;
    }
  }

  /**
   * Retrieves X-Target-Subscription-Key from OpenAPI spec for given URL
   *
   * @param targetUrl - The target API URL
   * @returns Subscription key or null if not found
   */
  private getTargetSubscriptionKey(targetUrl: string): string | null {
    try {
      return this.stateService.getSubscriptionKeyForUrl(targetUrl);
    } catch (error) {
      console.error('❌ Error retrieving target subscription key:', error);
      return null;
    }
  }

  /**
   * Determines if a request should be proxied through APIM
   *
   * @param url - The request URL
   * @returns True if request should be proxied
   */
  private shouldProxyRequest(url: string): boolean {
    try {
      // Skip OAuth/OIDC authentication requests
      if (this.isOAuthRequest(url)) {
        console.log('⏭️ Skipping OAuth request:', url);
        return false;
      }

      // Proxy all other requests
      return true;
    } catch (error) {
      console.warn('⚠️ Error checking if URL should be proxied:', error);
      return false;
    }
  }

  /**
   * Checks if URL is an OAuth/OIDC authentication request
   *
   * @param url - The request URL
   * @returns True if URL is an OAuth request
   */
  private isOAuthRequest(url: string): boolean {
    try {
      const urlLower = url.toLowerCase();

      // Common OAuth/OIDC patterns
      const oauthPatterns = [
        'login.microsoftonline.com',
        'oauth2/token',
        'oauth2/v2.0/token',
        'oauth2/authorize',
        'oauth2/v2.0/authorize',
        '.auth0.com',
        'accounts.google.com',
        'login.live.com',
        'signin.aws.amazon.com'
      ];

      return oauthPatterns.some(pattern => urlLower.includes(pattern));
    } catch (error) {
      console.warn('⚠️ Error checking OAuth request:', error);
      return false;
    }
  }

  /**
   * Cleanup method to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
